import React, { useState, useEffect } from 'react';
import { Card, Modal, Button as BootstrapButton } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { savePendingAction } from '../../utils/pendingAction';
import { useCachedImageUrl } from '../../hooks/useCachedImageUrl';
import { getPrimaryImageForColor, getColorTaggedImage } from '../../utils/productImagesByColor';
import {
  getProductSellingPrice,
  getProductDiscountPercent,
  hasActiveProductDiscount,
} from '../../utils/productPricing';
import './ProductCard.css';

const ProductCard = ({ product, buttonText = 'Add to Cart' }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  // Use in_wishlist from API when present (products list returns it); otherwise local state only
  const [inWishlist, setInWishlist] = useState(product.in_wishlist === true);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [productVariants, setProductVariants] = useState([]);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [availableColors, setAvailableColors] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [hasAvailableVariants, setHasAvailableVariants] = useState(false);
  const [productImagesForVariants, setProductImagesForVariants] = useState([]);
  const imageUrl = product.primary_image || '/placeholder.png';
  const { src: cachedImageSrc, loading: imageLoading } = useCachedImageUrl(imageUrl);
  const displayImageSrc = imageLoading ? '/placeholder.png' : (cachedImageSrc || imageUrl || '/placeholder.png');
  // Selling price: strike × (1 − %) when discount active, else catalog price
  const finalPrice = getProductSellingPrice(product);

  // Rating display removed from cards — kept only on product detail reviews section
  const categoryName = product.category_name || product.category?.name || '';
  const displayName = product.listing_color_name
    ? `${product.name} - ${product.listing_color_name}`
    : product.name;

  // Sync in_wishlist from product when API provides it (no per-product check API call)
  useEffect(() => {
    if (product.in_wishlist === true || product.in_wishlist === false) {
      setInWishlist(product.in_wishlist === true);
    }
  }, [product.in_wishlist]);

  const listingColorId =
    product.listing_color_id != null && product.listing_color_id !== ''
      ? Number(product.listing_color_id)
      : null;

  const productDetailPath =
    listingColorId != null
      ? `/products/${product.id}?color=${listingColorId}`
      : `/products/${product.id}`;

  const handleCardClick = (e) => {
    // Don't navigate if clicking on buttons, links, or wishlist button
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('.product-wishlist-btn')) {
      return;
    }
    navigate(productDetailPath);
  };

  const fetchProductVariants = async () => {
    try {
      setLoadingVariants(true);
      const response = await api.get(`/products/${product.id}`);
      const variants = response.variants || response.product?.variants || [];
      setProductVariants(variants);
      setProductImagesForVariants(response.images || []);
      
      // Check if there are any variants with stock
      const variantsWithStock = variants.filter(v => v.stock_quantity > 0);
      const hasVariants = variants.length > 0;
      setHasAvailableVariants(hasVariants && variantsWithStock.length > 0);
      
      // Extract unique colors (all colors, not just with stock)
      const allColors = [...new Set(variants.map(v => v.color_id).filter(Boolean))];
      setAvailableColors(allColors);
      
      // Default to listing color (from expanded products page) when present
      const colorsWithStock = allColors.filter(colorId => {
        return variants.some(v => v.color_id === colorId && v.stock_quantity > 0);
      });
      const preferredListing =
        product.listing_color_id != null &&
        allColors.some((c) => Number(c) === Number(product.listing_color_id))
          ? Number(product.listing_color_id)
          : null;
      const firstColor =
        preferredListing != null
          ? preferredListing
          : colorsWithStock.length > 0
            ? colorsWithStock[0]
            : allColors.length > 0
              ? allColors[0]
              : null;

      if (firstColor != null) {
        setSelectedColor(firstColor);
        const colorVariants = variants.filter(v => v.color_id === firstColor);
        const allSizesForColor = [...new Set(colorVariants.map(v => v.size).filter(Boolean))];
        setAvailableSizes(allSizesForColor);

        const firstVariantWithStock = colorVariants.find(v => v.stock_quantity > 0);
        const defaultSize = firstVariantWithStock
          ? firstVariantWithStock.size
          : (allSizesForColor.length > 0 ? allSizesForColor[0] : null);
        setSelectedSize(defaultSize);
      } else {
        setAvailableSizes([]);
        setSelectedSize(null);
      }
    } catch (error) {
      console.error('Error fetching variants:', error);
      toast.error('Failed to load product variants');
    } finally {
      setLoadingVariants(false);
    }
  };

  const handleAddToCartClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (product.combo_only) {
      toast.error(
        product.combo_only_message ||
          'This product is only available as part of a combo and cannot be purchased separately.'
      );
      return;
    }

    if (!isAuthenticated) {
      savePendingAction({ type: 'addToCart', productId: product.id });
      toast.info('Please log in to add items to your cart');
      setTimeout(() => navigate('/login'), 400);
      return;
    }

    setLoadingVariants(true);
    try {
      await fetchProductVariants();
      setShowVariantModal(true);
    } finally {
      setLoadingVariants(false);
    }
  };

  const addToCart = async (variantId) => {
    try {
      setAddingToCart(true);
      await api.post('/cart', {
        product_id: product.id,
        variant_id: variantId,
        quantity: 1,
      });
      toast.success('Added to cart');
      setShowVariantModal(false);
      // Trigger cart count update in navbar
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleModalAddToCart = async () => {
    if (!selectedColor || !selectedSize) {
      toast.error('Please select color and size');
      return;
    }

    const variant = productVariants.find(
      v => v.color_id === selectedColor && v.size === selectedSize && v.stock_quantity > 0
    );

    if (!variant) {
      toast.error('Selected variant is not available');
      return;
    }

    await addToCart(variant.id);
  };

  const handleColorChange = (colorId) => {
    setSelectedColor(colorId);

    const colorVariants = productVariants.filter(v => v.color_id === colorId);
    const sizesForColor = [...new Set(colorVariants.map(v => v.size).filter(Boolean))];
    setAvailableSizes(sizesForColor);

    const firstVariantWithStock = colorVariants.find(v => v.stock_quantity > 0);
    const defaultSize = firstVariantWithStock
      ? firstVariantWithStock.size
      : (sizesForColor.length > 0 ? sizesForColor[0] : null);
    setSelectedSize(defaultSize);
  };

  const handleViewProduct = () => {
    setShowVariantModal(false);
    navigate(productDetailPath);
  };

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      savePendingAction({ type: 'addToWishlist', productId: product.id });
      toast.info('Please log in to use the wishlist');
      setTimeout(() => navigate('/login'), 400);
      return;
    }

    try {
      if (inWishlist) {
        await api.delete(`/wishlist/${product.id}`);
        setInWishlist(false);
        toast.success('Removed from wishlist');
        // Trigger wishlist count update in navbar
        window.dispatchEvent(new Event('wishlistUpdated'));
      } else if (product.combo_only) {
        toast.error(
          product.combo_only_message ||
            'This product is only available as part of a combo and cannot be added to wishlist for separate purchase.'
        );
      } else {
        await api.post('/wishlist', { product_id: product.id });
        setInWishlist(true);
        toast.success('Added to wishlist');
        // Trigger wishlist count update in navbar
        window.dispatchEvent(new Event('wishlistUpdated'));
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to update wishlist';
      if (errorMessage.includes('already in wishlist')) {
        setInWishlist(true);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  return (
    <>
    <Card className="product-card" onClick={handleCardClick}>
      <div className={`product-image-wrapper ${imageLoading ? 'is-loading' : ''}`}>
        {imageLoading ? (
          <div className="product-image-skeleton" aria-hidden="true" />
        ) : (
          <img src={displayImageSrc} alt={displayName || 'Product'} />
        )}
        
        {/* Badge priority: Sale > New (Combo cards use Combo badge) */}
        <div className="product-badges">
          {product.is_sale === 1 || product.is_sale === true ? (
            <span className="product-badge product-badge-sale">Sale</span>
          ) : product.is_new === 1 || product.is_new === true ? (
            <span className="product-badge product-badge-new">New</span>
          ) : null}
        </div>
        
        <button 
          className={`product-wishlist-btn ${inWishlist ? 'in-wishlist' : ''}`}
          onClick={handleWishlistToggle}
          disabled={false}
          title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill={inWishlist ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        </button>
      </div>
      <Card.Body>
        <h5 className="product-name">{displayName}</h5>
        {categoryName && <p className="product-category">{categoryName}</p>}
        <div className="product-price-section">
          <div className="product-price">
            <span className="current-price">₹{finalPrice.toFixed(2)}</span>
            {(() => {
              const strike = parseFloat(product.strike_through_price) || 0;
              const discounted = hasActiveProductDiscount(product);
              // When discount active: show strike + % OFF. When no discount: optional strike if MRP > price
              if (!(strike > finalPrice && finalPrice > 0)) return null;
              const offPct = getProductDiscountPercent(product);
              const onSale = product.is_sale === 1 || product.is_sale === true;
              return (
                <>
                  <span className="original-price">
                    ₹{strike.toFixed(2)}
                  </span>
                  {(discounted || onSale) && offPct > 0 ? (
                    <span className="product-discount-label">{offPct}% OFF</span>
                  ) : null}
                </>
              );
            })()}
          </div>
        </div>
        <button 
          className="view-product-btn"
          onClick={handleAddToCartClick}
          disabled={loadingVariants}
        >
          {loadingVariants ? 'Loading...' : product.combo_only ? 'Combo only' : buttonText}
        </button>
      </Card.Body>
    </Card>

    {/* Variant Selection Modal */}
    <Modal show={showVariantModal} onHide={() => setShowVariantModal(false)} centered>
      <Modal.Header closeButton className="modal-header-custom">
        <Modal.Title className="text-md">Select Variant</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingVariants ? (
          <div className="text-center py-3">Loading variants...</div>
        ) : productVariants.length === 0 ? (
          <div className="text-center py-3">
            <p>This product has no variants available.</p>
          </div>
        ) : (
          <>
            {productImagesForVariants.length > 0 && (
              <div className="mb-3 text-center px-1">
                <img
                  src={getPrimaryImageForColor(
                    productImagesForVariants,
                    selectedColor,
                    product.primary_image
                  )}
                  alt={displayName || 'Product'}
                  className="w-100 rounded"
                  style={{ maxHeight: 220, objectFit: 'cover' }}
                />
              </div>
            )}
            {productVariants && productVariants.length > 0 && availableColors.length > 0 && (
              <div className="mb-4">
                <label className="variation-label">Variations:</label>
                <div className="color-swatches">
                  {productVariants
                    .filter(
                      (v, idx, self) => self.findIndex((x) => x.color_id === v.color_id) === idx
                    )
                    .map((variant) => {
                      // Check if this color has any available stock
                      const colorVariants = productVariants.filter(
                        (v) => v.color_id === variant.color_id
                      );
                      const hasStock = colorVariants.some((v) => v.stock_quantity > 0);
                      const isSelected = selectedColor === variant.color_id;
                      const swatchImage = getColorTaggedImage(
                        productImagesForVariants,
                        variant.color_id
                      );

                      return (
                        <button
                          key={variant.color_id}
                          type="button"
                          className={`color-swatch ${swatchImage ? 'color-swatch-image' : ''} ${
                            isSelected ? 'active' : ''
                          } ${!hasStock ? 'unavailable' : ''}`}
                          style={
                            swatchImage
                              ? undefined
                              : { backgroundColor: variant.color_hex || '#ccc' }
                          }
                          onClick={() => hasStock && handleColorChange(variant.color_id)}
                          disabled={!hasStock}
                          title={
                            hasStock ? variant.color_name : `${variant.color_name} - Out of Stock`
                          }
                        >
                          {swatchImage && (
                            <img
                              src={swatchImage}
                              alt={variant.color_name || 'Color'}
                              className="color-swatch-img"
                            />
                          )}
                          {!hasStock && (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="48"
                              height="48"
                              viewBox="0 0 48 48"
                              fill="none"
                              className="color-unavailable-line"
                            >
                              <path
                                d="M-1 0.000183105L48.3426 49.3428"
                                stroke="#E9E9E9"
                                strokeWidth="1.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeDasharray="6 6"
                              />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {availableSizes && availableSizes.length > 0 && (
              <div className="mb-4">
                <label className="size-label">Size:</label>
                <div className="size-buttons">
                  {availableSizes.map((size) => {
                    const variant = productVariants.find(
                      (v) => (!selectedColor || v.color_id === selectedColor) && v.size === size
                    );
                    const inStock = variant && variant.stock_quantity > 0;
                    const isSelected = selectedSize === size;

                    return (
                      <button
                        key={size}
                        className={`size-button ${isSelected ? 'selected' : ''} ${
                          !inStock ? 'out-of-stock' : ''
                        }`}
                        disabled={!inStock}
                        onClick={() => inStock && setSelectedSize(size)}
                        style={{ position: 'relative' }}
                      >
                        <span className={!inStock ? 'size-label-struck' : ''}>{size}</span>
                        {!inStock && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="50"
                            height="50"
                            viewBox="0 0 50 50"
                            fill="none"
                            className="size-out-of-stock-line"
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              pointerEvents: 'none'
                            }}
                          >
                            <path
                              d="M5 5L45 45"
                              stroke="#999"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeDasharray="4 4"
                            />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <BootstrapButton variant="outline-secondary" onClick={handleViewProduct}>
          View Product
        </BootstrapButton>
        <BootstrapButton 
          variant="primary" 
          className='bg-black border-none capitalize'
          onClick={handleModalAddToCart}
          disabled={!hasAvailableVariants || !selectedColor || !selectedSize || addingToCart}
        >
          {addingToCart ? 'Adding...' : hasAvailableVariants ? 'Add to Cart' : 'Unavailable'}
        </BootstrapButton>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default ProductCard;



